import React, { Fragment, useEffect, useState } from 'react'

import {
  classnames,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { string, func, bool, shape } from 'prop-types'

import i18n from 'Pods/Shared/I18n'

import { NOOP } from 'Utils/stableDefaults'

import ModelCardActionButton from './Components/ModelCardActionButton'
import WithListingPageContext from '../../WithListingPageContext'

function ModelCardActionButtons({
  id,
  isLoggedIn,
  isCartActionLoading = false,
  isFreeModel,
  isOnCart,
  isOnWishlist,
  isPurchased = false,
  isWishListActionLoading = false,
  onAddToCart = NOOP,
  onAddToWishlist = NOOP,
  onPreviewClick,
  onRemoveFromCart = NOOP,
  onRemoveFromWishlist = NOOP,
  isGalleryOpened = false,
  isTitleVisible,
  isSimilarSearchPossible = false,
  keywords = '',
  category = '',
  downloadData = null,
  onDownload = NOOP,
  locale = 'en',
}) {
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    setIsLoaded(true)
  }, [])

  if (!isLoaded) {
    return null
  }

  const hasDirectDownload = !!downloadData?.filesHref

  function renderDownloadButton(className) {
    return (
      <ModelCardActionButton
        className={className}
        id={id}
        icon="download"
        name="download"
        tooltipContent={i18n.t('listingPage.download.tooltip', { ns: 'rails', lng: locale })}
        onClick={() => onDownload(downloadData.filesHref)}
      />
    )
  }

  function renderFixedButton() {
    if (hasDirectDownload) {
      return renderDownloadButton(classnames(
        'card-3d-model__fixed-button',
        isTitleVisible && 'card-3d-model__fixed-button--with-title',
        isGalleryOpened && 'card-3d-model__fixed-button--hidden'
      ))
    }

    if (isFreeModel) {
      return null
    }

    if (isPurchased) {
      return (
        <ModelCardActionButton
          className={classnames(
            'card-3d-model__fixed-button',
            isTitleVisible && 'card-3d-model__fixed-button--with-title',
            isGalleryOpened && 'card-3d-model__fixed-button--hidden'
          )}
          id={id}
          icon="inbox"
          name="purchased"
          tooltipContent="Purchased"
          href="/profile/purchases"
          linkAttributes={{ target: '_blank' }}
        />
      )
    }

    if (isOnCart) {
      return (
        <ModelCardActionButton
          className={classnames(
            'card-3d-model__fixed-button',
            isTitleVisible && 'card-3d-model__fixed-button--with-title',
            isGalleryOpened && 'card-3d-model__fixed-button--hidden'
          )}
          icon="shopping-basket-remove"
          id={id}
          name="remove-from-cart"
          onClick={onRemoveFromCart}
          tooltipContent="Remove from cart"
          isLoading={isCartActionLoading}
        />
      )
    }

    if (isOnWishlist) {
      return (
        <ModelCardActionButton
          className={classnames(
            'card-3d-model__fixed-button',
            isTitleVisible && 'card-3d-model__fixed-button--with-title',
            isGalleryOpened && 'card-3d-model__fixed-button--hidden'
          )}
          icon="bookmark-remove"
          id={id}
          name="remove-from-wish-list"
          onClick={onRemoveFromWishlist}
          tooltipContent="Remove from wishlist"
          isLoading={isWishListActionLoading}
        />
      )
    }

    return null
  }

  function handleRedirectToSimilarPage() {
    if (window.customEventTracker) {
      window.customEventTracker({
        name: 'find_similar',
        eventData: {
          search_term: keywords || category || '',
          model_id: id,
        },
        customAttributes: {
          event_source: 'model_card',
        },
      })
    }

    const currentUrl = new URL(window.location.href)
    currentUrl.searchParams.set('similar_model_id', id)

    window.open(currentUrl.toString(), '_blank')
  }

  return (
    <Fragment>
      <div
        className={classnames(
          'card-3d-model__action-buttons',
          isTitleVisible && 'card-3d-model__action-buttons--with-title',
        )}
      >
        <div className="card-3d-model__action-buttons-group">
          <ModelCardActionButton
            icon="image-collection-outlined"
            id={id}
            name="open-gallery"
            onClick={onPreviewClick}
            tooltipContent="Preview"
          />
          {isSimilarSearchPossible && (
            <ModelCardActionButton
              icon="image-search"
              id={id}
              name="see-similar-models"
              onClick={handleRedirectToSimilarPage}
              tooltipContent="See similar models"
              actionType="button"
            />
          )}
        </div>
        <div className={classnames(
          'card-3d-model__action-buttons-group',
          isOnWishlist && 'card-3d-model__action-buttons-group--on-wish-list',
          isOnCart && 'card-3d-model__action-buttons-group--on-cart',
        )}
        >
          {hasDirectDownload && renderDownloadButton()}
          {!hasDirectDownload && isPurchased && (
            <ModelCardActionButton
              id={id}
              icon="inbox"
              name="purchased"
              tooltipContent="Purchased"
              href="/profile/purchases"
              linkAttributes={{ target: '_blank' }}
            />
          )}
          {!hasDirectDownload && !isFreeModel && !isPurchased && (
            <Fragment>
              {!isOnWishlist && (
                <ModelCardActionButton
                  id={id}
                  className={(!isLoggedIn && 'js-auth-control') || ''}
                  icon="bookmark-add"
                  name="add-to-wish-list"
                  onClick={onAddToWishlist}
                  tooltipContent="Add to wishlist"
                  isLoading={isWishListActionLoading}
                />
              )}
              {!isOnCart && (
                <ModelCardActionButton
                  icon="shopping-basket-add"
                  id={id}
                  name="add-to-cart"
                  onClick={onAddToCart}
                  tooltipContent="Add to cart"
                  isLoading={isCartActionLoading}
                />
              )}
            </Fragment>
          )}
        </div>
      </div>
      {renderFixedButton()}
    </Fragment>
  )
}

ModelCardActionButtons.propTypes = {
  id: string.isRequired,
  isLoggedIn: bool.isRequired,
  isCartActionLoading: bool,
  isFreeModel: bool.isRequired,
  isOnCart: bool.isRequired,
  isOnWishlist: bool.isRequired,
  isPurchased: bool,
  onAddToCart: func,
  onAddToWishlist: func,
  onPreviewClick: func.isRequired,
  onRemoveFromWishlist: func,
  onRemoveFromCart: func,
  isWishListActionLoading: bool,
  isGalleryOpened: bool,
  isTitleVisible: bool.isRequired,
  isSimilarSearchPossible: bool,
  keywords: string,
  category: string,
  downloadData: shape({ filesHref: string, kind: string }),
  onDownload: func,
  locale: string,
}

const mapStateToProps = ({
  selectedFilters, downloadAccess, openDownloadModal, locale,
}, { id }) => ({
  keywords: selectedFilters.keywords,
  downloadData: downloadAccess?.[id],
  onDownload: openDownloadModal,
  locale,
})

export default WithListingPageContext(mapStateToProps)(ModelCardActionButtons)
